import { createAsyncThunk } from "@reduxjs/toolkit";

import {
    GenerateSCNRequest,
    GetSCNParams,
    SCNReportLegacyAPI,
    SCNReportsResponse,
    SCNReportAPI,
    UpdateSCNLegacyRequest,
    UpsertSCNRequest,
} from "../../../../../api/scnReports";

import { MessageResponse } from "../../../../../api/common";


/**
 * =========================================
 * V1 Thunks
 * =========================================
 */

export const fetchSCNReportsLegacyThunk = createAsyncThunk<
    SCNReportsResponse,
    { case_id: string } & GetSCNParams,
    { rejectValue: string }
>(
    "reports/fetchSCN",
    async (params, { rejectWithValue }) => {
        try {
            return await SCNReportLegacyAPI.get_scn(params);
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch SCN reports");
        }
    }
);

export const fetchSinglePartySCNReportLegacyThunk = createAsyncThunk<
    SCNReportsResponse,
    { case_id: string; report_id: string },
    { rejectValue: string }
>(
    "reports/fetchSinglePartySCN",
    async ({ case_id, report_id }, { rejectWithValue }) => {
        try {
            return await SCNReportLegacyAPI.get_scn({ case_id, report_id });
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch SCN report for Party");
        }
    }
);

export const createSCNReportLegacyThunk = createAsyncThunk<
    MessageResponse,
    { case_id: string; request: GenerateSCNRequest },
    { rejectValue: string }
>(
    "reports/createSCN",
    async ({ case_id, request }, { rejectWithValue }) => {
        try {
            return await SCNReportLegacyAPI.create_scn(case_id, request);
        } catch (error: any) {
            return rejectWithValue(error.message || "Generate SCN report submission failed");
        }
    }
);

export const upsertSCNReportLegacyThunk = createAsyncThunk<
    MessageResponse,
    { case_id: string; report_id: string; request: UpdateSCNLegacyRequest },
    { rejectValue: string }
>(
    "reports/upsertSCN",
    async ({ case_id, report_id, request }, { rejectWithValue }) => {
        try {
            return await SCNReportLegacyAPI.upsert_scn(case_id, report_id, request);
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to update SCN report");
        }
    }
);


/**
 * =========================================
 * V2 Thunks
 * =========================================
 */

export const fetchSCNReportsThunk = createAsyncThunk<
    SCNReportsResponse,
    { case_id: string } & GetSCNParams,
    { rejectValue: string }
>(
    "reports/fetchSCNV2",
    async (params, { rejectWithValue }) => {
        try {
            return await SCNReportAPI.get_scn_reports(params);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch SCN reports"
            );
        }
    }
);

export const fetchSinglePartySCNReportThunk = createAsyncThunk<
    SCNReportsResponse,
    { case_id: string; report_id: string },
    { rejectValue: string }
>(
    "reports/fetchSinglePartySCNV2",
    async ({ case_id, report_id }, { rejectWithValue }) => {
        try {
            return await SCNReportAPI.get_scn_reports({ case_id, report_id });
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch SCN report for Party"
            );
        }
    }
);

export const createSCNReportThunk = createAsyncThunk<
    MessageResponse,
    { case_id: string; request: GenerateSCNRequest },
    { rejectValue: string }
>(
    "reports/createSCNV2",
    async ({ case_id, request }, { rejectWithValue }) => {
        try {
            return await SCNReportAPI.generate_scn_report(case_id, request);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Generate SCN report submission failed"
            );
        }
    }
);

export const upsertSCNReportThunk = createAsyncThunk<
    MessageResponse,
    { case_id: string; report_id: string; request: UpsertSCNRequest },
    { rejectValue: string }
>(
    "reports/upsertSCNV2",
    async ({ case_id, report_id, request }, { rejectWithValue }) => {
        try {
            return await SCNReportAPI.upsert_scn_report(case_id, report_id, request);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to update SCN report"
            );
        }
    }
);